import { useState } from 'react';
import { orgs, ApiClientError, type MembershipRole } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { useAuth } from '../hooks/useAuth';
import { useProject } from '../hooks/useProject';
import { formatDate } from '../lib/format';
import { ASSIGNABLE_ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS, can } from '../lib/roles';
import { Alert, Badge, Card, Empty, Modal, Spinner } from '../components/ui';

/**
 * Team settings for the active project's organization: members with roles,
 * pending invites, and an invite form. Role management is owner/admin only —
 * the buttons hide for everyone else, and the server enforces the same matrix
 * (plus the cross-seat rules: admins can't touch owners, the last owner is
 * immovable).
 */
export function Team() {
  const { account } = useAuth();
  const { project } = useProject();
  const orgId = project?.organization_id;

  const members = useResource(() => orgs.members(orgId!), [orgId]);
  const manager = can(project?.role, 'manage_team');
  const invites = useResource(
    () => (manager ? orgs.invites(orgId!) : Promise.resolve([])),
    [orgId, manager]
  );

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MembershipRole>('developer');
  const [busy, setBusy] = useState(false);

  if (!project || !orgId) return <Spinner label="Loading your team…" />;

  function failed(caught: unknown, fallback: string) {
    setError(caught instanceof ApiClientError ? caught.message : fallback);
  }

  async function sendInvite() {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await orgs.invite(orgId!, inviteEmail, inviteRole);
      setNotice(
        result.attached
          ? `${inviteEmail} already had an account and has been added to the team.`
          : `Invitation sent to ${inviteEmail}.`
      );
      setInviting(false);
      setInviteEmail('');
      setInviteRole('developer');
      await Promise.all([members.reload(), invites.reload()]);
    } catch (caught) {
      failed(caught, 'Could not send the invite');
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(userId: string, role: MembershipRole) {
    setError(null);
    try {
      await orgs.changeRole(orgId!, userId, role);
      await members.reload();
    } catch (caught) {
      failed(caught, 'Could not change the role');
      await members.reload();
    }
  }

  async function remove(userId: string, email: string) {
    if (!confirm(`Remove ${email} from ${project!.organization_name}?`)) return;
    setError(null);
    try {
      await orgs.removeMember(orgId!, userId);
      await members.reload();
    } catch (caught) {
      failed(caught, 'Could not remove the member');
    }
  }

  async function revokeInvite(inviteId: string) {
    setError(null);
    try {
      await orgs.revokeInvite(orgId!, inviteId);
      await invites.reload();
    } catch (caught) {
      failed(caught, 'Could not revoke the invite');
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>Team</h1>
        <p>
          People with access to <strong>{project.organization_name}</strong> and all of its
          projects. Roles decide what each member can do.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <Card
        title="Members"
        actions={
          manager && (
            <button className="btn btn-primary btn-sm" onClick={() => setInviting(true)}>
              Invite member
            </button>
          )
        }
      >
        {members.loading ? (
          <Spinner />
        ) : !members.data?.length ? (
          <Empty>No members.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Joined</th>
                  {manager && <th />}
                </tr>
              </thead>
              <tbody>
                {members.data.map((member) => {
                  const self = member.user_id === account?.id;
                  return (
                    <tr key={member.user_id}>
                      <td>
                        {member.name ?? <span className="muted">Unnamed</span>}
                        <div className="muted" style={{ fontSize: 12.5 }}>
                          {member.email}
                          {self ? ' (you)' : ''}
                        </div>
                      </td>
                      <td>
                        {manager && !self ? (
                          <select
                            value={member.role}
                            aria-label={`Role for ${member.email}`}
                            onChange={(event) =>
                              changeRole(member.user_id, event.target.value as MembershipRole)
                            }
                          >
                            {ASSIGNABLE_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Badge tone={member.role === 'owner' ? 'accent' : 'neutral'}>
                            {ROLE_LABELS[member.role]}
                          </Badge>
                        )}
                      </td>
                      <td className="muted">{formatDate(member.joined_at)}</td>
                      {manager && (
                        <td style={{ textAlign: 'right' }}>
                          {!self && (
                            <button
                              className="btn btn-sm"
                              onClick={() => remove(member.user_id, member.email)}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {manager && (
        <Card title="Pending invites">
          {invites.loading ? (
            <Spinner />
          ) : !invites.data?.length ? (
            <Empty>No pending invites.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Sent</th>
                    <th>Expires</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {invites.data.map((invite) => (
                    <tr key={invite.id}>
                      <td>{invite.email}</td>
                      <td>
                        <Badge>{ROLE_LABELS[invite.role]}</Badge>
                      </td>
                      <td className="muted">{formatDate(invite.created_at)}</td>
                      <td className="muted">{formatDate(invite.expires_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-sm" onClick={() => revokeInvite(invite.id)}>
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {inviting && (
        <Modal
          title="Invite a team member"
          onClose={() => setInviting(false)}
          footer={
            <>
              <button className="btn" onClick={() => setInviting(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={busy || !inviteEmail}
                onClick={sendInvite}
              >
                {busy ? 'Sending…' : 'Send invite'}
              </button>
            </>
          }
        >
          <label className="field">
            <span>Email address</span>
            <input
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="teammate@company.com"
              autoFocus
            />
          </label>
          <label className="field">
            <span>Role</span>
            <select
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value as MembershipRole)}
            >
              {ASSIGNABLE_ROLES.filter((role) => role !== 'owner' || project.role === 'owner').map(
                (role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                )
              )}
            </select>
            <span className="muted" style={{ fontSize: 12.5 }}>
              {ROLE_DESCRIPTIONS[inviteRole]}
            </span>
          </label>
          <p className="muted" style={{ fontSize: 12.5 }}>
            If they already have a Difference account they're added immediately; otherwise they'll
            get an email and join when they register with this address.
          </p>
        </Modal>
      )}
    </>
  );
}
